/**
 * Input abstraction. The sim only ever sees a ControlInput; keyboard, gamepad and the
 * scripted harness all produce one. Analog shaping lives here so keyboard play still
 * gets ramped, non-binary control.
 */

import { clamp, clamp01, damp, shapeAxis } from '../core/math';

export interface ControlInput {
  /** -1 left .. +1 right — commands lean, which the bike achieves by countersteering. */
  lean: number;
  /** -1 back .. +1 forward — rider weight shift. */
  weightShift: number;
  brakeFront: number;
  brakeRear: number;
  pedal: number;
  hop: boolean;
  /** Airborne yaw for whips/spins. */
  airYaw: number;
  /** 0..1 — the rider pulling on the bars against the pedals. Its own axis rather than a
   * deep weight shift, because being behind the saddle and manualling are different
   * things a rider does for different reasons and often not together. */
  manual: number;
  resetPressed: boolean;
}

export function makeControlInput(): ControlInput {
  return {
    lean: 0,
    weightShift: 0,
    brakeFront: 0,
    brakeRear: 0,
    pedal: 0,
    hop: false,
    airYaw: 0,
    manual: 0,
    resetPressed: false,
  };
}

const KEY_MAP: Record<string, string> = {
  KeyA: 'left', ArrowLeft: 'left',
  KeyD: 'right', ArrowRight: 'right',
  KeyW: 'fwd', ArrowUp: 'fwd',
  KeyS: 'back', ArrowDown: 'back',
  ShiftLeft: 'pedal', ShiftRight: 'pedal',
  KeyJ: 'brakeFront',
  KeyK: 'brakeRear',
  Space: 'hop',
  KeyQ: 'yawLeft',
  KeyE: 'yawRight',
  KeyF: 'manual',
  KeyR: 'reset',
};

/**
 * How fast a digital key ramps into its analog value, 1/s.
 *
 * The bar asks for input to visible response in under ~80 ms, and a damp() rate is a time
 * constant: 9/s is 111 ms to 63%, which was over budget before the sim had even looked at
 * the number.
 *
 * 16/s was 62 ms and the arithmetic behind it was wrong in a way only measurement finds:
 * this lag and the sim's own steering lag are in *series*, so they add rather than one
 * fitting inside the other. Measured on the bench, a step at the stick reached 63% of the
 * steady bar angle in 46 ms with the sim driven directly — put a 62 ms keyboard ramp in
 * front of that and the player sees ~110 ms, comfortably over budget. 26/s is 38 ms,
 * which put the pair at ~84 ms — still over, by the same arithmetic that was used to
 * justify it. 32/s is 31 ms and lands at ~77, inside the bar with a little margin, and is
 * still an unmistakable ramp rather than a step.
 * It can be this fast without reading twitchy because the *bike's* response is governed
 * further downstream by rider.leanCommandRate, which is a tenth as quick — what this
 * number buys is only that the bars start moving when the player asks.
 *
 * Steering is the axis the number is spent on, because steering is what the player sees
 * respond. Weight shift is a slower movement on a real bike and reads wrong if it snaps.
 */
const LEAN_RATE = 32;
const SHIFT_RATE = 9;
/**
 * Brakes ramp fast here and are shaped and lagged in the sim (see updateBrakes in
 * physics.ts). Two lags in series would put the brake outside the latency budget, and the
 * one that belongs to the *bike* rather than to the keyboard is the one to keep, because
 * a gamepad trigger has no ramp at all and must feel the same.
 *
 * 24/s was 42 ms, and the same series arithmetic that caught the steering axis applies
 * here: 42 ms of keyboard plus the 55 ms `brakeRampTime` the pads take to bite is 97 ms
 * to 63% of the commanded torque, which is over the bar's 80 ms with the budget spent on
 * the half that is not the bike. 40/s is 25 ms and lands the pair at 80. The sim-side lag
 * is the one worth having because it is what a gamepad trigger will feel too.
 */
const BRAKE_RATE = 40;

export class InputManager {
  readonly input = makeControlInput();
  private readonly down = new Set<string>();
  private padIndex: number | null = null;
  /** When set, keyboard/gamepad are ignored and this drives the sim (capture harness). */
  scripted: ((t: number, out: ControlInput) => void) | null = null;

  attach(target: EventTarget = window): void {
    target.addEventListener('keydown', (e) => {
      const k = KEY_MAP[(e as KeyboardEvent).code];
      if (k) {
        this.down.add(k);
        e.preventDefault();
      }
    });
    target.addEventListener('keyup', (e) => {
      const k = KEY_MAP[(e as KeyboardEvent).code];
      if (k) {
        this.down.delete(k);
        e.preventDefault();
      }
    });
    window.addEventListener('gamepadconnected', (e) => {
      this.padIndex = (e as GamepadEvent).gamepad.index;
    });
    window.addEventListener('gamepaddisconnected', () => {
      this.padIndex = null;
    });
    window.addEventListener('blur', () => this.down.clear());
  }

  update(dt: number, simTime: number): ControlInput {
    const i = this.input;
    if (this.scripted) {
      this.scripted(simTime, i);
      return i;
    }

    const pad = this.padIndex !== null ? navigator.getGamepads?.()[this.padIndex] : null;
    if (pad) {
      i.lean = shapeAxis(pad.axes[0] ?? 0);
      i.weightShift = shapeAxis(-(pad.axes[1] ?? 0));
      i.brakeFront = clamp01(pad.buttons[7]?.value ?? 0);
      i.brakeRear = clamp01(pad.buttons[6]?.value ?? 0);
      i.pedal = clamp01(pad.buttons[0]?.value ?? 0);
      // One button for hop, pump and preload, because on a real bike they are one
      // movement — held is the rider driving down into the bike, released is the pop, and
      // which of the three it turns out to be is decided by where on the terrain the
      // player lets go. See updatePump in physics.ts.
      i.hop = (pad.buttons[1]?.pressed ?? false) || (pad.buttons[5]?.pressed ?? false);
      // Manual on the left bumper: it is a held state, not a tap, so it wants a button the
      // thumb never has to leave the stick for.
      i.manual = clamp01(pad.buttons[4]?.value ?? (pad.buttons[4]?.pressed ? 1 : 0));
      i.airYaw = shapeAxis(pad.axes[2] ?? 0);
      i.resetPressed = pad.buttons[9]?.pressed ?? false;
      return i;
    }

    // Keyboard: ramp digital keys into analog so control is never binary.
    const leanTarget = (this.down.has('right') ? 1 : 0) - (this.down.has('left') ? 1 : 0);
    const shiftTarget = (this.down.has('fwd') ? 1 : 0) - (this.down.has('back') ? 1 : 0);
    i.lean = damp(i.lean, leanTarget, LEAN_RATE, dt);
    i.weightShift = damp(i.weightShift, shiftTarget, SHIFT_RATE, dt);
    i.brakeFront = damp(i.brakeFront, this.down.has('brakeFront') ? 1 : 0, BRAKE_RATE, dt);
    i.brakeRear = damp(i.brakeRear, this.down.has('brakeRear') ? 1 : 0, BRAKE_RATE, dt);
    i.pedal = damp(i.pedal, this.down.has('pedal') ? 1 : 0, 6, dt);
    i.hop = this.down.has('hop');
    i.manual = damp(i.manual, this.down.has('manual') ? 1 : 0, 11, dt);
    i.airYaw = damp(i.airYaw, (this.down.has('yawRight') ? 1 : 0) - (this.down.has('yawLeft') ? 1 : 0), 8, dt);
    i.resetPressed = this.down.has('reset');
    return i;
  }
}
